'use client';

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import { tw } from '../lib/twind';
import type { CallQualification, CallInteractionResult } from '@callixbrasil/client-sdk';

type DragPosition = {
  x: number;
  y: number;
};

type PointerData = {
  pointerId: number | null;
  offsetX: number;
  offsetY: number;
};

type DraggableQualificationModalProps = {
  isOpen: boolean;
  result: CallInteractionResult;
  onChangeResult: (result: CallInteractionResult) => void;
  successQualifications: CallQualification[];
  discardQualifications: CallQualification[];
  selectedQualificationId: number | null;
  onSelectQualification: (qualificationId: number) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  portalTarget?: HTMLElement | null;
};

const VIEWPORT_MARGIN = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function DraggableQualificationModal({
  isOpen,
  result,
  onChangeResult,
  successQualifications,
  discardQualifications,
  selectedQualificationId,
  onSelectQualification,
  onSubmit,
  isSubmitting,
  errorMessage,
  portalTarget = null,
}: DraggableQualificationModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<PointerData>({ pointerId: null, offsetX: 0, offsetY: 0 });
  const dragMovedRef = useRef(false);

  const [position, setPosition] = useState<DragPosition>({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);

  const options = result === 'success' ? successQualifications : discardQualifications;
  const hasOptions = options.length > 0;

  const clampPositionToViewport = useCallback((proposed: DragPosition) => {
    if (typeof window === 'undefined') {
      return proposed;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const element = containerRef.current;
    const width = element?.offsetWidth ?? 400;
    const height = element?.offsetHeight ?? 500;

    const minX = VIEWPORT_MARGIN;
    const minY = VIEWPORT_MARGIN;
    const maxX = Math.max(minX, viewportWidth - width - VIEWPORT_MARGIN);
    const maxY = Math.max(minY, viewportHeight - height - VIEWPORT_MARGIN);

    return {
      x: clamp(proposed.x, minX, maxX),
      y: clamp(proposed.y, minY, maxY),
    };
  }, []);

  // Centralizar modal ao abrir
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      const element = containerRef.current;
      const width = element?.offsetWidth ?? 400;
      const height = element?.offsetHeight ?? 500;
      const centerX = (window.innerWidth - width) / 2;
      const centerY = (window.innerHeight - height) / 2;
      setPosition(clampPositionToViewport({ x: centerX, y: centerY }));
    }
  }, [isOpen, clampPositionToViewport]);

  // Ajustar posição ao redimensionar viewport
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return;
    }

    const handleViewportChange = () => {
      setPosition((prev) => clampPositionToViewport(prev));
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isOpen, clampPositionToViewport]);

  // Gerenciar drag
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      dragMovedRef.current = true;

      const modalWidth = containerRef.current?.offsetWidth ?? 0;
      const modalHeight = containerRef.current?.offsetHeight ?? 0;
      const nextX = event.clientX - pointerRef.current.offsetX;
      const nextY = event.clientY - pointerRef.current.offsetY;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const maxX = viewportWidth - modalWidth - VIEWPORT_MARGIN;
      const maxY = viewportHeight - modalHeight - VIEWPORT_MARGIN;
      const safeMaxX = Math.max(VIEWPORT_MARGIN, maxX);
      const safeMaxY = Math.max(VIEWPORT_MARGIN, maxY);

      setPosition({
        x: clamp(nextX, VIEWPORT_MARGIN, safeMaxX),
        y: clamp(nextY, VIEWPORT_MARGIN, safeMaxY),
      });
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (pointerRef.current.pointerId !== event.pointerId) {
        return;
      }

      setIsDragging(false);
      pointerRef.current = { pointerId: null, offsetX: 0, offsetY: 0 };
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  const beginDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }

    const element = containerRef.current;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    pointerRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    dragMovedRef.current = false;
    setIsDragging(true);
  }, []);

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      ref={containerRef}
      className={tw`fixed z-[9999] flex flex-col overflow-hidden rounded-xl border border-slate-300 bg-white shadow-2xl`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '420px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 32px)',
      }}
    >
      {/* Header - Área arrastável */}
      <div
        onPointerDown={beginDrag}
        className={tw`flex cursor-move items-center justify-center border-b border-slate-200 bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3 text-white`}
      >
        <h3 className={tw`text-sm font-bold`}>Qualificar Chamada</h3>
      </div>

      {/* Body */}
      <div className={tw`flex-1 space-y-4 overflow-y-auto p-5`}>
        <div className={tw`text-center`}>
          <p className={tw`text-sm text-slate-600`}>
            Esta etapa é obrigatória para registrar o desfecho do atendimento.
          </p>
        </div>

        {/* Seletor de resultado */}
        <div className={tw`space-y-2`}>
          <p className={tw`text-sm font-semibold text-slate-700`}>Resultado da chamada</p>
          <div className={tw`grid grid-cols-2 gap-3`}>
            <button
              type="button"
              onClick={() => onChangeResult('success')}
              className={tw`${
                result === 'success'
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500'
                  : 'border-slate-200 bg-white hover:border-emerald-300'
              } flex flex-col items-center justify-center gap-2 rounded-lg border px-4 py-3 transition`}
            >
              <FiCheckCircle
                className={tw`h-6 w-6 ${result === 'success' ? 'text-emerald-600' : 'text-slate-400'}`}
              />
              <span
                className={tw`text-sm font-semibold ${result === 'success' ? 'text-emerald-700' : 'text-slate-600'}`}
              >
                Sucesso
              </span>
            </button>

            <button
              type="button"
              onClick={() => onChangeResult('failure')}
              className={tw`${
                result === 'failure'
                  ? 'border-rose-500 bg-rose-50 ring-2 ring-rose-500'
                  : 'border-slate-200 bg-white hover:border-rose-300'
              } flex flex-col items-center justify-center gap-2 rounded-lg border px-4 py-3 transition`}
            >
              <FiXCircle className={tw`h-6 w-6 ${result === 'failure' ? 'text-rose-600' : 'text-slate-400'}`} />
              <span className={tw`text-sm font-semibold ${result === 'failure' ? 'text-rose-700' : 'text-slate-600'}`}>
                Falha
              </span>
            </button>
          </div>
        </div>

        {/* Lista de qualificações */}
        <div className={tw`space-y-2`}>
          <p className={tw`text-sm font-semibold text-slate-700`}>Selecione uma qualificação</p>
          {hasOptions ? (
            <div className={tw`max-h-64 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2`}>
              {options.map((qualification) => {
                const isSelected = selectedQualificationId === qualification.id;
                return (
                  <button
                    key={qualification.id}
                    type="button"
                    onClick={() => onSelectQualification(qualification.id)}
                    className={tw`${
                      isSelected
                        ? 'border-blue-400 bg-blue-100 ring-2 ring-blue-400'
                        : 'border-slate-200 bg-white hover:border-blue-300'
                    } w-full rounded-lg border px-4 py-3 text-left transition`}
                  >
                    <p className={tw`text-sm font-semibold ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
                      {qualification.name}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={tw`flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700`}>
              <FiAlertCircle className={tw`h-5 w-5 flex-shrink-0`} />
              <span>Nenhuma qualificação disponível para este tipo de resultado.</span>
            </div>
          )}
        </div>

        {/* Mensagem de erro */}
        {errorMessage && (
          <div className={tw`flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700`}>
            <FiAlertCircle className={tw`h-5 w-5 flex-shrink-0`} />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>

      {/* Footer com botão de enviar */}
      <div className={tw`border-t border-slate-200 bg-slate-50 px-5 py-4`}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!hasOptions || !selectedQualificationId || isSubmitting}
          className={tw`flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {isSubmitting && (
            <svg
              className={tw`h-4 w-4 animate-spin text-white`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className={tw`opacity-25`} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className={tw`opacity-75`}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {isSubmitting ? 'Enviando...' : 'Confirmar Qualificação'}
        </button>
      </div>
    </div>
  );

  // Renderizar modal como primeiro filho do body usando portal
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, portalTarget ?? document.body);
  }

  return null;
}
